import React, { useState } from "react";
import classnames from "classnames";
import { useHistory } from "react-router-dom";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../api";
import {
  AppPermissions,
  State,
  useConfiguration,
  useHasPermission,
} from "../../core";
import { PaginatedApiResponse } from "..";
import {
  requestFirebaseNotificationPermission,
  onMessageListener,
} from "../../firebaseInit";
import { NotificationData } from "../api/models/notification";
import { useSelector } from "react-redux";
import Popup from "../../components/popup";
import "./notification.scss";
import { formatDistanceStrict } from "date-fns";
import { RouteKeys } from "../../containers/routes/route-keys";

const Notifications: React.FunctionComponent<{ className?: string }> = (
  props
) => {
  const { openModal, setSelectedAction } = useConfiguration(api);

  const profile = useSelector((state: State) => state.authUser.profile);
  const [page, setPage] = useState(1);
  const [token, setFirebaseToken] = useState<any>("");
  const history = useHistory();
  const canViewLeadDetails = useHasPermission(AppPermissions.ViewLeadDetails);

  const [notifications, setNotifications] = useState<NotificationData[]>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [unReadCount, setUnReadCount] = useState<number>(0);

  const [requestModal, setRequestModal] = useState<{
    isOpen?: boolean;
    configId?: string;
    leadId?: string;
    id?: string;
  }>({});

  const { data, reload, loaded, loading } = useDataLoader<
    PaginatedApiResponse<NotificationData> & { unReadCount: number }
  >(
    (currentPage: number) => api.notification.getNotifications(currentPage, 10),
    [page]
  );

  React.useEffect(() => {
    if (!loaded) return;
    const notificationList =
      Number(page) <= 1
        ? [...(data?.data || [])]
        : [...(notifications ? notifications : []), ...data?.data];

    setNotifications(notificationList);
    setUnReadCount(data?.unReadCount);
    setTotalCount(data?.count);
  }, [data, loaded]);

  const loadMoreOrder = () => {
    const newPage = changePage("pageDown", page);
    setPage(newPage);
    reload(newPage);
  };

  const changePage = (direction: any, currentPage: number) => {
    let newPage = currentPage;
    if (direction === "pageDown") newPage = currentPage + 1;
    if (direction === "pageUp") newPage = currentPage - 1;
    return newPage;
  };

  React.useEffect(() => {
    if (token) {
      onMessageListener()
        .then((payload: any) => {
          appendLastRecord();
        })
        .catch((err) => {
          // toastr.error('Error!', 'Something went wrong.');
        });
    }
  });

  const appendLastRecord = async () => {
    const lastRecord = await api.notification.getNotifications(1, 1);
    const notificationList = !!notifications?.length ? [...notifications] : [];
    notificationList.unshift(lastRecord?.data?.[0]);
    setNotifications(notificationList);
  };

  React.useEffect(() => {
    requestFirebaseNotificationPermission()
      .then((firebaseToken) => {
        setFirebaseToken(firebaseToken);

        api.notification.registerClient({
          app: "web-app-crm",
          userId: profile?._id || "",
          token: firebaseToken,
          platform: "web",
          deviceId: "",
        });
      })
      .catch((err) => {
        return err;
      });
  }, []);

  const handleClick = async () => {
    if (unReadCount) {
      const notificationList = !!notifications?.length
        ? [...notifications]
        : [];
      setNotifications(
        notificationList?.map((x) => {
          return {
            ...x,
            isRead: true,
          };
        })
      );
      await api.notification.markAllRead();
    }
  };

  const handleSingleClick = async (id: string) => {
    if (unReadCount) {
      const notificationList = !!notifications?.length
        ? [...notifications]
        : [];
      setNotifications(
        notificationList?.map((x) => {
          return {
            ...x,
            isRead: x._id?.toString() === id?.toString() ? false : x.isRead,
          };
        })
      );
      await api.notification.markOneRead(id);
    }
  };

  const onRowClick = async (notification: NotificationData) => {
    if (canViewLeadDetails) {
      handleSingleClick(notification._id);
      if (notification?.data?.type === "config") {
        if (notification?.data.customer) {
          const lead = await api.lead.getLead(notification?.data?.leadId);
          history.push(
            `${RouteKeys.Requirement.replace(":id", notification?.data?.id!)}`
          );
        } else {
          const config = await api.configuration.getConfiguration(
            notification?.data?.leadId,
            notification?.data?.id
          );
          if (config) {
            history.push(
              `${RouteKeys.Requirement.replace(":id", config?.data?._id!)}`
            );
          }
        }

        history.push(
          `${RouteKeys.Lead360View}/${notification?.data?.customerId || ""}`
        );
      } else if (
        notification?.data?.type === "payment" &&
        notification?.title === "Refund Request"
      ) {
        if (notification?.data?.leadId && notification?.data?.id) {
          const config = await api.configuration.getConfiguration(
            notification?.data?.leadId,
            notification?.data?.id
          );
          if (config) {
            history.push(
              `${RouteKeys.Requirement.replace(":id", config?.data?._id!)}`
            );

            setSelectedAction("paymentSummary");
          }
        }
        history.push(
          `${RouteKeys.Lead360View}/${notification?.data?.customerId || ""}`
        );
      } else if (notification?.data?.type === "specialApprovalRequest") {
        setRequestModal({
          isOpen: true,
          leadId: notification?.data?.leadId,
          configId: notification?.data?.configId,
          id: notification?.data?.id,
        });
        reload(1);
      } else if (notification?.data?.leadId || notification?.data?.id) {
        history.push(
          `${RouteKeys.Leads}/${
            notification?.data?.leadId || notification?.data?.id
          }`
        );
      }
    }
  };

  const renderNotification = (notification: NotificationData) => (
    <li
      className={classnames({
        "notification-item justify-content-between": true,
        "bg-limeyellow": !notification.isRead,
      })}
      key={notification._id}
    >
      <div
        className="notification-message cursor-pointer"
        onClick={() => onRowClick(notification)}
      >
        {notification.message}
        <span className="notification-time">
          {formatDistanceStrict(new Date(notification.createdAt), new Date())}
        </span>
      </div>
      {!notification.isRead && (
        <div
          className={classnames({
            "notification-dot bg-gray-400 rounded-circle mx-1 cursor-pointer": true,
            "bg-blue": !notification.isRead,
          })}
          title="Mark as Read"
          onClick={() => handleSingleClick(notification._id)}
        ></div>
      )}
    </li>
  );

  return (
    <>
      <div className="position-relative">
        <div
          className={`notification ${props.className || ""}`}
          id="notificationIcon"
        >
          {unReadCount > 0 && (
            <span className="notification__count position-absolute bg-red text-white font-xs rounded-pill px-1">
              {unReadCount}
            </span>
          )}
          <i className="icon-bell text-gray-400 font-base "></i>
        </div>
        <Popup target="notificationIcon" cuePos="pos1" align="right" right="5">
          <div className="notification-box" style={{ overflowY: totalCount === 0 ? 'hidden' : 'scroll' }}>
            <div className="notification-header" style={{ fontSize: totalCount === 0 ? '1.0em' : '1.25em' }}>
             {totalCount > 0 ? 'Notifications' : 'No Notifications'}
              {unReadCount > 0 && (
                <span
                  className="float-lg-right font-sm mt-1 cursor-pointer"
                  onClick={handleClick}
                >
                  Mark all as Read
                </span>
              )}
            </div>
            <ul className="notification-list">
              {notifications?.map(renderNotification)}
            </ul>
            {!loading &&
              notifications &&
              (totalCount || 0) > 0 &&
              (totalCount || 0) > notifications?.length && (
                <div
                  className="bg-gray-100 notification__load-more py-2 w-100 d-flex align-items-center justify-content-center cursor-pointer mt-0"
                  onClick={loadMoreOrder}
                >
                  <span className="px-2 font-xxs text-uppercase">
                    Load More
                  </span>
                </div>
              )}
            {loading && (
              <div className="bg-gray-100 notification__load-more py-2 w-100 d-flex align-items-center justify-content-center mt-0">
                <span className="px-2 font-xxs text-uppercase ">
                  Loading...
                </span>
              </div>
            )}
          </div>
        </Popup>
      </div>
    </>
  );
};

export default Notifications;
